import React from "react"
import { Modal, Form } from "react-bootstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import PrimaryButton from "../subComponents/PrimaryButton"
import { toast } from "react-toastify"
import { request } from "../../services/request"
import { useParams } from "react-router-dom"
import { PaySetting, PaySettings } from "../../services/authinterface"

type Props = {
  show: boolean
  onClose: () => void
  fetchCustomerBills: any
}

function BillModel({ show, onClose, fetchCustomerBills }: Props) {
  const [paySetting, setPaySetting] = React.useState<PaySetting | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const param = useParams()
  const validationSchema = Yup.object().shape({
    bill_type: Yup.string()
      .oneOf(["Sensor", "SOS"], "Invalid Product type")
      .required("Please select Product type"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Please enter amount")
      .min(0, "Amount must be positive"),
    notes: Yup.string(),
    bill_date: Yup.string().required("Please select bill date"),
    product_count: Yup.number().required("Please enter Product Count").min(1, "Product Count must be at least 1")
  })

  const fetchPaySettingDetails = React.useCallback(async () => {
    try {

      const response = await request<PaySettings>({
        url: `/adminPaySetting/getPaySettingDetails`,
        method: "GET",
      })

      if (response.data) {
        setPaySetting(response.data)
      } else {
        toast.error("Failed to fetch paySetting: " + response.message)
      }
    } catch (error) {
    }
  }, [])

  React.useEffect(() => {
    if(show){
      fetchPaySettingDetails()
    }
  }, [fetchPaySettingDetails, show])

  const formik = useFormik({
    initialValues: {
      bill_type: "Sensor",
      amount: "",
      notes: "",
      bill_date: "",
      product_count: 1
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      try {
        const payload = {
          userId: param.id,
          type: values.bill_type,
          amount: values.amount,
          date: values.bill_date,
          notes: values.notes,
          product_count: values.product_count
        }
        const response = await request({
          url: `/payment/DirectSubscribe`,
          method: "POST",
          data: payload,
        })
        if (response.data) {
          toast.success(response.message)
          onClose()
          formik.resetForm()
          fetchCustomerBills()
        } else {
          toast.error(response.message)
        }
      } catch (error: any) {
        toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
      } finally {
        setIsSubmitting(false)
      }
    },
  })
  
  const { values, setFieldValue } = formik;

  React.useEffect(() => {
    if (!paySetting) return;

    if (values.bill_type === "SOS") {
      if (paySetting.status === "1") {
        setFieldValue("amount", paySetting.SOS_bill_amount);
      } else {
        setFieldValue("amount", paySetting.test_SOS_bill_amount);
      }
    } else if (values.bill_type === "Sensor") {
      if (paySetting.status === "1") {
        setFieldValue("amount", paySetting.Sensor_bill_amount);
      } else {
        setFieldValue("amount", paySetting.test_Sensor_bill_amount);
      }
    }
  }, [values.bill_type, paySetting, setFieldValue]);
  

  return (
    <Modal
      className='comman-modal'
      show={show}
      onHide={() => {
        onClose()
        formik.resetForm()
      }}
      centered
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton className='comman-modal-header'>
          <Modal.Title className='comman-modal-title'>Add Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body className='comman-modal-body'>
          <div className='login-input-section input-section mb-3'>
            {/* <Form.Label>Bill Type</Form.Label> */}
            <div>
              <label htmlFor='newPassword'>Product Type</label>
            </div>
            <Form.Select
              name='bill_type'
              value={formik.values.bill_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.bill_type && formik.touched.bill_type}
            >
              <option value='Sensor'>Sensor</option>
              <option value='SOS'>SOS</option>
            </Form.Select>
            <span className='text-red-500 text-xs'>{formik.errors.bill_type}</span>
          </div>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label>
                Amount{" "}
                <span className='font-sm'>
                  (Set amount <span className='text-danger'>0</span> to create an invoice without
                  collecting payment)
                </span>
              </label>
            </div>
            <Form.Control
              type='number'
              name='amount'
              placeholder='Please Enter Amount'
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.amount && formik.touched.amount}
            />
            <span className='text-red-500 text-xs'>{formik.errors.amount}</span>
          </div>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label>
                Product Count
              </label>
            </div>
            <Form.Control
              type='number'
              name='product_count'
              placeholder='Please Enter Product Count'
              value={formik.values.product_count}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              min={1}
              isInvalid={!!formik.errors.product_count && formik.touched.product_count}
            />
            <span className='text-red-500 text-xs'>{formik.errors.product_count}</span>
          </div>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label>Bill Date</label>
            </div>
            <Form.Control
              type='date'
              name='bill_date'
              value={formik.values.bill_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.bill_date && formik.touched.bill_date}
            />
            {/* <span className='text-red-500 text-xs'>{formik.errors.bill_date}</span> */}
            <span className='text-red-500 text-xs'>
              {formik.touched.bill_date && formik.errors.bill_date ? formik.errors.bill_date : null}
            </span>
          </div>
          <div className='login-input-section input-section mb-3'>
            <div>
              <label>Notes</label>
            </div>
            <Form.Control
              as='textarea'
              rows={3}
              name='notes'
              placeholder='Please Enter Notes'
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.notes && formik.touched.notes}
            />
            <span className='text-red-500 text-xs'>{formik.errors.notes}</span>
          </div>

          <PrimaryButton disabled={isSubmitting} type='submit' label={isSubmitting ? "Generating..." : 'Generate Product Bill'} className={`editFormButton ${isSubmitting ? "cursor-not-allowed" : ""}`} />
        </Modal.Body>
      </Form>
    </Modal>
  )
}

export default BillModel
